import { Button } from "@/modules/shared/ui/components/button"
import { useCompanyLookup } from "@/modules/organizations/hooks/useCompanyLookup"
import { toast } from "sonner"
import type { OrganizationFormData } from "@/modules/organizations/entities/Organization"

interface RegisterOrganizationWebsiteStepProps {
  websiteUrl: string
  onWebsiteUrlChange: (url: string) => void
  onDetailsLoaded: (details: OrganizationFormData) => void
  onNext: () => void
  onCancel: () => void
}

export function RegisterOrganizationWebsiteStep({
  websiteUrl,
  onWebsiteUrlChange,
  onDetailsLoaded,
  onNext,
  onCancel,
}: RegisterOrganizationWebsiteStepProps) {
  const { fetchCompanyDetails, isLoading: isLookingUp } = useCompanyLookup()

  const handleNext = async () => {
    if (websiteUrl.trim()) {
      const details = await fetchCompanyDetails(websiteUrl)
      if (details) {
        onDetailsLoaded(details)
        toast.success("Company details loaded successfully")
      } else {
        toast.info("Could not fetch details. Please enter manually.")
      }
    }
    onNext()
  }

  return (
    <>
      <p className="text-center mb-8 text-muted-foreground">
        Enter your company website to auto-fill details (optional)
      </p>
      <div className="space-y-4 max-w-2xl mx-auto">
        <div className="space-y-2">
          <label className="text-foreground font-medium">
            Company Website (Optional)
          </label>
          <input
            type="url"
            placeholder="https://example.com"
            value={websiteUrl}
            onChange={(e) => onWebsiteUrlChange(e.target.value)}
            className="w-full p-3 border border-border rounded-md focus:border-primary focus:ring-ring"
          />
          <p className="text-sm text-muted-foreground">
            We'll use AI to extract company information from your website
          </p>
        </div>

        <div className="flex justify-end space-x-4 pt-6">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="px-6 py-2 text-muted-foreground hover:text-foreground"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleNext}
            disabled={isLookingUp}
            className="px-6 py-2 bg-primary text-primary-foreground rounded-md font-semibold disabled:opacity-50 flex items-center gap-2"
          >
            {isLookingUp ? (
              <>
                <svg
                  className="animate-spin h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Fetching details from your website...
              </>
            ) : (
              "Next: Company Details"
            )}
          </Button>
        </div>
      </div>
    </>
  )
}
